/**
 * WorkspaceCopilotFloating — small floating action button rendered on every
 * dashboard page. Click opens a popover with the compact Workspace Copilot.
 *
 * Kept as a standalone widget so mounting from DashboardLayout adds
 * discoverability everywhere without cluttering individual pages.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sparkles, X, Expand } from 'lucide-react';
import WorkspaceCopilot from '@/components/dashboard/WorkspaceCopilot';

const HIDDEN_PATHS = ['/dashboard/ask', '/dashboard/subscription', '/dashboard/settings'];

const WorkspaceCopilotFloating = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const panelRef = useRef(null);

  // Hide on pages where it'd overlap another primary CTA
  const hidden = HIDDEN_PATHS.some((p) => location.pathname.startsWith(p));

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Close on Esc
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    if (open) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (hidden) return null;

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 h-12 w-12 rounded-full bg-violet-600 hover:bg-violet-700 text-white shadow-lg shadow-violet-950/50 flex items-center justify-center transition transform hover:scale-105"
          title="Ask your workspace"
          data-testid="workspace-copilot-fab"
        >
          <Sparkles className="h-5 w-5" />
        </button>
      )}
      {open && (
        <div
          ref={panelRef}
          className="fixed bottom-5 right-5 z-50 w-[min(560px,calc(100vw-2rem))] max-h-[min(720px,calc(100vh-2rem))] overflow-y-auto rounded-lg shadow-2xl shadow-black/50 border border-[#1F2A4D] bg-[#0A1024]"
          data-testid="workspace-copilot-floating-panel"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#1F2A4D] sticky top-0 bg-[#0F1730] z-10">
            <div className="flex items-center gap-1.5 text-xs text-slate-200 font-medium">
              <Sparkles className="h-3.5 w-3.5 text-violet-300" />
              Ask your workspace
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => { navigate('/dashboard/ask'); setOpen(false); }}
                className="p-1.5 rounded text-slate-400 hover:text-slate-100 hover:bg-[#131C3A]"
                title="Open full page"
                data-testid="workspace-copilot-fab-expand"
              >
                <Expand className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1.5 rounded text-slate-400 hover:text-slate-100 hover:bg-[#131C3A]"
                title="Close"
                data-testid="workspace-copilot-fab-close"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <div className="p-2">
            <WorkspaceCopilot compact />
          </div>
        </div>
      )}
    </>
  );
};

export default WorkspaceCopilotFloating;
